import { RefObject, useEffect, useRef, useState } from 'react';
import { VariableSizeList as List } from 'react-window';
import { ReactEditor, useSlate } from 'slate-react';

import { MentionsState } from '@web/richText/TextEditorInner/hooks/useMentions';
import { useRichTextContext } from '@web/richText/components/RichTextProvider/RichTextProvider';

import {
  initialPosition,
  ITEM_HEIGHT,
  MAX_LIST_HEIGHT,
  MAX_LIST_WIDTH,
  MAX_WRAPPER_WIDTH,
  MIN_SPACE_TOP,
  PADDING,
} from './mentionSuggestions.consts';
import { ListSettings, Position, Target } from './mentionSuggestions.types';

export const useListSettings = (itemCount: number): ListSettings => {
  const itemKey = (index: number, data: MentionsState): number => {
    return data.filteredPersonas[index].id;
  };

  const itemSize = (): number => ITEM_HEIGHT;
  const height = Math.min(MAX_LIST_HEIGHT, itemCount * ITEM_HEIGHT);
  const width = MAX_LIST_WIDTH;

  return {
    height,
    itemCount,
    itemKey,
    itemSize,
    width,
  };
};

export const useScrollToItem = (activeIndex: number): RefObject<List> => {
  const ref = useRef<List>();

  useEffect(() => {
    if (ref?.current) {
      ref.current.scrollToItem(activeIndex);
    }
  }, [activeIndex]);

  return ref;
};

export const usePosition = (itemCount: number, target: Target): Position => {
  const editor = useSlate();
  const { editorContainer } = useRichTextContext();
  const [position, setPosition] = useState<Position>(initialPosition);

  useEffect(() => {
    try {
      // TODO try to store target's rect in RichTextProvider,
      // so editor and target won't be required here
      const domRange = ReactEditor.toDOMRange(editor, target);
      // FIXME rect has inconsistent value (left value may change up to ~10px)
      // this is probably due to some Slate internal calculations
      // happens only when in first place mention list has one item and then multiple
      const rect = domRange.getBoundingClientRect();

      const listHeight = Math.min(MAX_LIST_HEIGHT, itemCount * ITEM_HEIGHT);

      const listOffset = rect.left + MAX_WRAPPER_WIDTH;
      const editorContainerOffset = editorContainer.rect.left + editorContainer.rect.width;
      const exceedingOffset = Math.max(0, listOffset - editorContainerOffset);

      // by default display mentions list below target - `@sth` marker
      const scrollTop = window.scrollY;
      let top = rect.top + PADDING * 2.5 + scrollTop;
      const viewportHeight = window.document.documentElement.clientHeight;
      // but if the position exceeds view port size
      const fitsViewport = viewportHeight + scrollTop > top + listHeight + MIN_SPACE_TOP;
      if (!fitsViewport) {
        // let the list be displayed above the marker
        top = rect.top + scrollTop - listHeight - PADDING * 2;
      }

      setPosition({
        left: rect.left + window.pageXOffset - exceedingOffset,
        top,
      });
    } catch (e) {
      return;
    }
  }, [editor, editorContainer, itemCount, target]);

  return position;
};
